// Implements: F-UIX-019, F-RPT-009
/**
 * One "Exportar CSV" of a platform report (owner 2026-10-19): the download under way and the last
 * refusal, behind the button.
 *
 * A download is a plain `GET` through the shared client (`httpDownload`, the caller's `download`):
 * a refusal raises its `ApiError`, never a file holding the error body. The bytes are saved under
 * the server's file name, or `fallbackName` when the answer names none. A refusal (the row cap's
 * `422`, a `404`) is the server's sentence for the annex's codes and a generic one otherwise, and
 * nothing is saved. A second press while one download runs is ignored. No browser storage.
 */

import { useRef, useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import type { Download } from "../../../shared/api/httpClient";
import { useTranslation } from "../../../shared/i18n";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { saveBlob } from "../../../shared/ui/saveBlob";
import { ExportCsvButton } from "../components/ExportCsvButton";

export interface ExportCsvContainerProps {
  /** The download of what the screen shows, asked when the person presses the button. */
  readonly download: () => Promise<Download>;
  readonly fallbackName: string;
}

export function ExportCsvContainer({ download, fallbackName }: ExportCsvContainerProps): ReactElement {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const running = useRef(false);

  const run = async (): Promise<void> => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setFailure(null);
    try {
      const file = await download();
      saveBlob(file.blob, file.filename ?? fallbackName);
    } catch (cause) {
      setFailure(userMessage(asApiError(cause), t("reports.export.failed")));
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  return (
    <ExportCsvButton
      busy={busy}
      failure={failure}
      onExport={() => {
        void run();
      }}
    />
  );
}
